import React, { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { Mic, Music2, Type } from "lucide-react";
import { S, SERIF, SANS, IMG, Eyebrow, Reveal, H2, Section, Photo } from "./slUi";

const STEPS = [
  ["Truth", "Right now, some days feel heavy."],
  ["Nothing missing", "Nothing is missing from me."],
  ["Trust", "I can trust what I feel."],
  ["Identity", "I am someone who returns to myself."],
  ["Possibility", "I am free to become more."],
];
const ARC = ["opens", "builds", "softens", "space", "settles"];

const MusicArc = () => (
  <div data-testid="sl-journey-music-view">
    <Photo src={IMG.music} alt="Walking by the sea" ratio="aspect-[16/8]" className="mb-6" pos="center 60%" />
    <svg viewBox="0 0 500 160" className="w-full h-auto">
      <motion.path d="M0,140 C60,135 90,90 140,70 S220,20 260,40 S330,120 370,125 S440,95 500,100" fill="none" stroke={S.gold} strokeWidth="2"
        initial={{ pathLength: 0 }} whileInView={{ pathLength: 1 }} viewport={{ once: false }} transition={{ duration: 2.4, ease: "easeInOut" }} />
    </svg>
    <div className="mt-2 grid grid-cols-5 text-center text-[0.58rem] uppercase tracking-[0.22em]" style={{ fontFamily: SANS, color: S.dim }}>{ARC.map((a) => <span key={a}>{a}</span>)}</div>
    <p className="mt-6 text-base leading-relaxed" style={{ fontFamily: SANS, color: S.dim }}>The music isn't sitting behind the words. It creates the emotional environment, opening gently, building, softening, making space and settling. You don't analyse it. You feel the journey.</p>
  </div>
);

const AffirmationSteps = () => {
  const [i, setI] = useState(0);
  useEffect(() => { const t = setInterval(() => setI((x) => (x + 1) % STEPS.length), 3400); return () => clearInterval(t); }, []);
  return (
    <div data-testid="sl-journey-affirmation-view">
      <Photo src={IMG.affirm} alt="A quiet returning smile" ratio="aspect-[16/8]" className="mb-6" pos="center 30%" />
      <div className="flex flex-wrap gap-2">
        {STEPS.map(([s], n) => (
          <button key={s} type="button" onClick={() => setI(n)} data-testid={`sl-aff-step-${n}`} className="rounded-full px-3 py-1.5 text-[0.58rem] uppercase tracking-[0.22em] transition-colors duration-500"
            style={{ fontFamily: SANS, background: n === i ? S.gold : "transparent", color: n === i ? S.night : S.dim, border: `1px solid ${S.goldSoft}` }}>{s}</button>
        ))}
      </div>
      <>
        <motion.p key={i} initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0 }} transition={{ duration: 0.6 }}
          className="mt-8 min-h-[5.5rem] sm:min-h-[6.5rem] text-3xl sm:text-4xl font-light" style={{ fontFamily: SERIF }}>&ldquo;{STEPS[i][1]}&rdquo;</motion.p>
      </>
      <p className="mt-6 text-base leading-relaxed" style={{ fontFamily: SANS, color: S.dim }}>The language meets you where you are and gradually carries you somewhere else. Some words are clearly present, others become quieter. There is space, repetition, and moments where the music carries you. A journey through language, not a list of positive statements.</p>
    </div>
  );
};

const VoiceView = () => (
  <div data-testid="sl-journey-voice-view">
    <Photo src={IMG.voice} alt="Recording a voice note at home" ratio="aspect-[16/8]" className="mb-6" pos="center 30%" />
    <p className="text-3xl sm:text-4xl font-light leading-tight" style={{ fontFamily: SERIF }}>Your words. Your voice. <span className="" style={{ color: S.gold }}>Your inner dialogue.</span></p>
    <div className="mt-6 grid sm:grid-cols-2 gap-3">
      <div className="rounded-xl p-4" style={{ border: `1px solid ${S.goldSoft}` }}><p className="text-[0.58rem] uppercase tracking-[0.24em]" style={{ fontFamily: SANS, color: S.dim }}>A generic narrator</p><p className="mt-2 text-xl " style={{ fontFamily: SERIF, color: S.dim }}>"You trust yourself."</p></div>
      <div className="rounded-xl p-4" style={{ background: "rgba(224,169,122,0.12)", border: `1px solid ${S.gold}` }}><p className="text-[0.58rem] uppercase tracking-[0.24em]" style={{ fontFamily: SANS, color: S.gold }}>In your own voice</p><p className="mt-2 text-xl " style={{ fontFamily: SERIF }}>"I trust myself."</p></div>
    </div>
    <ol className="mt-6 space-y-2 text-sm" style={{ fontFamily: SANS, color: S.dim }}>
      <li>1 · Read a few calm lines aloud on your phone.</li>
      <li>2 · Soulumo learns the sound of your voice.</li>
      <li>3 · Your installations can speak to you in it.</li>
    </ol>
    <p className="mt-5 text-base leading-relaxed" style={{ fontFamily: SANS, color: S.dim }}>The voice that has spoken to you internally for years can begin speaking to you differently.</p>
  </div>
);

const TABS = [["music", "The music journey", Music2, MusicArc], ["affirmation", "The affirmation journey", Type, AffirmationSteps], ["voice", "The voice journey", Mic, VoiceView]];

export const Different = () => {
  const [tab, setTab] = useState("affirmation");
  const View = TABS.find((t) => t[0] === tab)[3];
  return (
    <Section tone="deep" id="sl-different" testId="sl-different" className="py-28 sm:py-36">
      <div className="max-w-5xl mx-auto">
        <Reveal><Eyebrow light>Why it feels different</Eyebrow></Reveal>
        <div className="mt-4 grid lg:grid-cols-2 gap-8 items-center">
          <Reveal delay={0.05}><H2>More than affirmations <span className="" style={{ color: S.gold }}>over music.</span></H2></Reveal>
          <Reveal delay={0.1}><div className="relative" data-testid="sl-different-photo">
            <Photo src={IMG.listen} alt="Listening, with more happening beneath the surface" ratio="aspect-[4/3]" pos="center 30%" />
            <div className="absolute inset-0 rounded-2xl" style={{ background: "linear-gradient(90deg, rgba(13,24,48,0.78), rgba(13,24,48,0) 62%)" }} />
            <div className="absolute left-5 bottom-5 space-y-2">
              {["Your voice", "The affirmation journey", "The music journey"].map((l, i) => (
                <p key={l} className="pl-3 text-[0.6rem] uppercase tracking-[0.3em]" style={{ fontFamily: SANS, color: S.cream, opacity: 1 - i * 0.2, marginLeft: i * 14, borderLeft: `1px solid ${S.gold}` }}>{l}</p>
              ))}
            </div>
          </div></Reveal>
        </div>
        <Reveal delay={0.1}><div className="mt-10 flex items-center gap-3 text-sm" style={{ fontFamily: SANS, color: S.dim }}>
          <span className="rounded-full px-3 py-1.5" style={{ border: `1px solid ${S.goldSoft}` }}>Affirmation</span>+<span className="rounded-full px-3 py-1.5" style={{ border: `1px solid ${S.goldSoft}` }}>Background music</span>
          <span className="hidden sm:inline">is how most affirmation audio works. Soulumo has an architecture: several journeys happening together.</span>
        </div></Reveal>
        <div className="mt-12 grid lg:grid-cols-[260px,1fr] gap-8">
          <div className="flex lg:flex-col gap-2 overflow-x-auto" role="tablist">
            {TABS.map(([k, l, Icon]) => (
              <button key={k} type="button" onClick={() => setTab(k)} data-testid={`sl-journey-tab-${k}`} role="tab" aria-selected={tab === k}
                className="shrink-0 flex items-center gap-3 rounded-xl px-4 py-3 text-left transition-colors duration-300"
                style={{ background: tab === k ? "rgba(224,169,122,0.14)" : "transparent", border: `1px solid ${tab === k ? S.gold : S.goldSoft}`, color: tab === k ? S.cream : S.dim, fontFamily: SANS, fontSize: "0.85rem" }}>
                <Icon size={16} color={S.gold} /> {l}
              </button>
            ))}
          </div>
          <>
            <motion.div key={tab} initial={{ opacity: 0, x: 14 }} animate={{ opacity: 1, x: 0 }} exit={{ opacity: 0, x: -10 }} transition={{ duration: 0.45 }} className="min-h-[520px]"><View /></motion.div>
          </>
        </div>
      </div>
    </Section>
  );
};
